/* Hook encargado de bajar una imagen

Autor: Benjamín León
Fecha: 2024-12-01
*/
import { useState } from 'react'

import { makeImage } from '../utils/makeImage'
import { getToken } from '../utils/utils'

import { makeUrlImageAttachment } from './connectionData'

const useImageFetcher = () => {
  const [errorImage, setErrorImage] = useState<Error | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const fetchImage = async (url: string) => {
    setLoading(true)

    try {
      url = makeImage(url)
      // Fetch the image as a blob
      // Debe ser sin proteccion en la api porque las imagenes son publicas
      const response = await fetch(url, { mode: 'cors' })
      const blob = await response.blob()

      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(blob)

      // Create an anchor element and set the download attribute with a default filename and extension
      const anchor = document.createElement('a')
      anchor.href = blobUrl
      anchor.download = `${url.split('/').pop() ?? 'download'}.jpg` // Default to .jpg or use your preferred extension
      document.body.appendChild(anchor)
      anchor.click()

      // Clean up by revoking the blob URL and removing the anchor element
      window.URL.revokeObjectURL(blobUrl)
      document.body.removeChild(anchor)
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorImage(err)
      } else {
        setErrorImage(new Error('An unknown error occurred'))
      }

      return
    } finally {
      setLoading(false)
    }
  }

  const fetchAttachment = async (id: string) => {
    setLoading(true)

    try {
      const url = makeUrlImageAttachment(id)
      // Fetch the image as a blob
      const token = getToken()

      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      }

      const updatedOptions: RequestInit = {
        headers,
      }

      const response = await fetch(url, updatedOptions)

      const blob = await response.blob()

      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(blob)
      // Open the document in a new tab
      window.open(blobUrl, '_blank')
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorImage(err)
      } else {
        setErrorImage(new Error('An unknown error occurred'))
      }

      return
    } finally {
      setLoading(false)
    }
  }

  // Exponemos estas variables al exterior
  return { errorImage, loading, fetchImage, fetchAttachment }
}
export default useImageFetcher
